import { useRef } from "react";
import { ZoomInIcon, ZoomOutIcon } from "lucide-react";
import { Button, Slider } from "web-design-system";
import { BannerImage } from "./banner-image";
import {
  BannerCrop,
  BannerUpload,
  clampCrop,
  getMaxZoom,
  panCrop,
} from "./crop";

// Fraction of the frame the image moves per arrow key press
const KEYBOARD_STEP = 0.02;

const ARROW_KEY_DIRECTIONS: Record<string, [number, number]> = {
  ArrowLeft: [1, 0],
  ArrowRight: [-1, 0],
  ArrowUp: [0, 1],
  ArrowDown: [0, -1],
};

/** Lets the user drag and zoom an uploaded image inside the 5:4 banner frame */
export function BannerCropEditor({
  upload,
  imageUrl,
  crop,
  onCropChange,
  onReplace,
  onDone,
}: {
  upload: BannerUpload;
  imageUrl: string;
  crop: BannerCrop;
  onCropChange: (crop: BannerCrop) => void;
  onReplace: () => void;
  onDone: () => void;
}) {
  const imageAspectRatio = upload.width / upload.height;
  const maxZoom = getMaxZoom(upload);
  // Where the pointer and the crop were when the drag started
  const drag = useRef<{ x: number; y: number; crop: BannerCrop } | null>(null);

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    const frame = event.currentTarget.getBoundingClientRect();
    const dx = (event.clientX - drag.current.x) / frame.width;
    const dy = (event.clientY - drag.current.y) / frame.height;
    onCropChange(panCrop(drag.current.crop, imageAspectRatio, dx, dy));
  };

  const endDrag = () => {
    drag.current = null;
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const direction = ARROW_KEY_DIRECTIONS[event.key];
    if (!direction) return;
    event.preventDefault();
    onCropChange(
      panCrop(
        crop,
        imageAspectRatio,
        direction[0] * KEYBOARD_STEP,
        direction[1] * KEYBOARD_STEP
      )
    );
  };

  return (
    <div className="grid grid-cols-4 gap-5">
      <div
        role="application"
        aria-label="Reposition banner. Drag or use the arrow keys."
        tabIndex={0}
        className="rounded-2xl overflow-hidden cursor-grab active:cursor-grabbing touch-none outline-none ring-4 ring-slate-500 focus-visible:ring-primary"
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          drag.current = { x: event.clientX, y: event.clientY, crop };
        }}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={onKeyDown}
      >
        <BannerImage
          banner={{ url: imageUrl, framing: { crop, imageAspectRatio } }}
          className="aspect-[5/4]"
        />
      </div>

      <div className="col-span-3 flex flex-col justify-between gap-4 max-w-sm">
        <div className="flex flex-col gap-1">
          <span className="font-semibold text-sm">Reposition your image</span>
          <span className="text-sm text-muted-foreground">
            Drag the image to choose the part your clients will see.
          </span>
        </div>

        {maxZoom > 1 && (
          <div className="flex items-center gap-3 text-muted-foreground">
            <ZoomOutIcon className="size-4 shrink-0" />
            <Slider
              aria-label="Zoom"
              min={1}
              max={maxZoom}
              step={0.01}
              value={[crop.zoom]}
              onValueChange={([zoom]) =>
                onCropChange(clampCrop({ ...crop, zoom }, imageAspectRatio))
              }
            />
            <ZoomInIcon className="size-4 shrink-0" />
          </div>
        )}

        <div className="flex gap-3">
          <Button type="button" variant="outline" size="sm" onClick={onReplace}>
            Choose another image
          </Button>
          <Button type="button" variant="secondary" size="sm" onClick={onDone}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}
